import React, { Component, ReactNode } from 'react';
import { Measurement } from '../../models/Measurement';
import MeasurementsTable from '../../features/measurements-table/MeasurementsTable';
import { SingleUser, CreateMeasurementRequest } from '../../api/types';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import { Api } from '../../api';
import { connect } from 'react-redux';
import { ApplicationState } from '../../app/store';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { AppNotificationType } from '../../models/AppNotification';
import { ConfirmModal } from '../../features/confirm-modal/ConfirmModal';
import { IDossierV2 } from '../../models/DossierV2';
import { MeasurementSplitLayout } from './MeasurementSplitLayout';
import './MeasurementOverview.scss';

interface MeasurementOverviewProps {
  dossier?: IDossierV2;
  user?: SingleUser;
}

interface MeasurementOverviewState {
  measurements: Measurement[];
  loading?: boolean;
  loadError?: string;
  repeatMeasurement?: Measurement;
  repeating?: boolean;
  deleteMeasurement?: Measurement;
  deleting?: boolean;
}

class MeasurementOverview extends Component<MeasurementOverviewProps, MeasurementOverviewState> {
  state: MeasurementOverviewState = {
    measurements: [],
  };

  componentDidMount(): void {
    this.loadMeasurements();
  }

  private loadMeasurements(): void {
    this.setState({ loading: true });
    const { dossier } = this.props;
    if (!dossier?.id) {
      this.setState({
        loading: false,
        loadError: 'Kan metingen niet ophalen omdat er geen dossier geselecteerd is'
      });
      return;
    }
    Api.measurements(dossier.id)
      .then(response => this.setState({ measurements: Measurement.fromResponse(response) }))
      .catch(err => {
        console.error('Failed to load measurements', err);
      })
      .finally(() => this.setState({ loading: false }));
  }

  handleRepeatMeasurement = (measurement: Measurement): void => {
    this.setState({ repeatMeasurement: measurement });
  };

  handleDeleteMeasurement = (measurement: Measurement): void => {
    this.setState({ deleteMeasurement: measurement });
  };

  closeRepeatModal = (): void => this.setState({ repeating: false, repeatMeasurement: undefined });

  closeDeleteModal = (): void => this.setState({ deleting: false, deleteMeasurement: undefined });

  repeatMeasurementConfirm = (): void => {
    const { dossier } = this.props;
    const measurement = this.state.repeatMeasurement;
    if (!dossier?.id) {
      NotificationHelper.toastError('Meting herhalen mislukt, omdat er geen dossier geselecteerd is.');
      return;
    }
    if (!measurement) {
      NotificationHelper.toastError('Meting herhalen mislukt, probeer het later opnieuw.');
      return;
    }

    this.setState({ repeating: true });
    const repeat: CreateMeasurementRequest = {
      type: measurement.type,
      question: measurement.question,
      start: measurement.start,
      end: measurement.end,
      forFile: dossier.id,
    };
    Api.createMeasurement(repeat)
      .then(() => {
        NotificationHelper.push(
          'Meting herhaald!',
          { type: AppNotificationType.SUCCESS, alwaysToast: true });
        this.loadMeasurements();
      })
      .catch(err => {
        NotificationHelper.toastError('Meting herhalen mislukt, probeer het later opnieuw.');
        console.error('Failed to repeat measurement', err);
      })
      .finally(() => this.closeRepeatModal());
  };

  deleteMeasurementConfirm = (): void => {
    const { dossier } = this.props;
    const measurement = this.state.deleteMeasurement;
    if (!dossier?.id) {
      NotificationHelper.toastError('Meting verwijderen mislukt, omdat er geen dossier geselecteerd is.');
      return;
    }
    if (!measurement) {
      NotificationHelper.toastError('Meting verwijderen mislukt door een onverwachte fout.');
      return;
    }

    this.setState({ deleting: true });
    Api.deleteMeasurement(measurement.id)
      .then(() => {
        NotificationHelper.push(
          'Meting verwijderd',
          { type: AppNotificationType.SUCCESS, alwaysToast: true });
        this.loadMeasurements();
      })
      .catch(err => {
        NotificationHelper.toastError('Meting verwijderen mislukt, probeer het later opnieuw.');
        console.error('Failed to delete measurement', err);
      })
      .finally(() => this.closeDeleteModal());
  };

  render(): ReactNode {
    const { measurements, loading, loadError, repeatMeasurement, repeating, deleteMeasurement, deleting } = this.state;
    const isEmpty: boolean = !loading && measurements.length === 0;

    return (
      <MeasurementSplitLayout>
        { loadError && <Alert variant="danger">{loadError}</Alert>}
        <MeasurementsTable measurements={measurements} onRepeatMeasurement={this.handleRepeatMeasurement} onDeleteMeasurement={this.handleDeleteMeasurement} />
        { loading && <Spinner animation="border" variant="primary" /> }
        { isEmpty && <Alert className="measurement-overview__info" variant="info">Nog geen metingen beschikbaar</Alert>}

        {
          !!repeatMeasurement &&
          <ConfirmModal
            title="Meting herhalen"
            body={<>Weet je zeker dat je de meting <strong>&apos;{repeatMeasurement?.question}&apos;</strong> wilt herhalen?</>}
            confirmLabel="Herhalen"
            confirmButtonStyle="default"
            onCancel={this.closeRepeatModal}
            onConfirm={this.repeatMeasurementConfirm}
            confirming={repeating}
          />
        }

        {
          !!deleteMeasurement &&
          <ConfirmModal
            title="Meting verwijderen"
            body={<>Weet je zeker dat je de meting <strong>&apos;{deleteMeasurement?.question}&apos;</strong> wilt verwijderen?</>}
            confirmLabel="Verwijderen"
            confirmButtonStyle="danger"
            onCancel={this.closeDeleteModal}
            onConfirm={this.deleteMeasurementConfirm}
            confirming={deleting}
          />
        }
      </MeasurementSplitLayout>
    );
  }
}

export default connect((state: ApplicationState) => ({
  dossier: state.session.dossier,
  user: state.session.user,
}))(MeasurementOverview);
